import { buildLepImageUrl } from '../store/utils';
import { useState, useRef } from 'react';
import './css/mcq-image-upload.scss';

export const McqImageUpload = function ({ name, label, optional = false, imageName, handleImage, altText = '', onAltTextChange, imageLocation }) {
  const [isImgPreview, setIsImgPreview] = useState(false);
  const [fileError, setFileError] = useState('');
  const [fileWarning, setFileWarning] = useState('');
  const imgRef = useRef(null);

  const ALLOWED_TYPES = ['image/jpeg', 'image/png'];
  const MAX_SIZE = 1 * 1024 * 1024;       // 1 MB — hard limit
  const WARN_SIZE = 500 * 1024;           // 500 KB — soft warning

  function handleUpload (input) {
    const file = input.target.files[0];
    if (!file) return;

    setFileError('');
    setFileWarning('');

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Invalid file type. Please upload a JPG or PNG image.');
      input.target.value = '';
      return;
    }

    if (file.size > MAX_SIZE) {
      setFileError('**File too large**. Images must be under 1MB. Please compress your file at squoosh.app and try again.');
      input.target.value = '';
      return;
    }

    if (file.size > WARN_SIZE) {
      setFileWarning('**Large file size**. This image is over 500KB. Consider compressing it to ensure faster page load times.');
    }

    const formData = new FormData();
    formData.append('upload-image', file);
    setIsImgPreview(true);
    handleImage({ key: name, action: 'upload', imageName: file.name, formData, imageLocation });
    previewImage(input);
  }

  function handleDelete () {
    handleImage({ key: name, action: 'delete' });
    setIsImgPreview(false);
    setFileError('');
    setFileWarning('');
  }

  function previewImage (input) {
    const reader = new FileReader();
    reader.readAsDataURL(input.target.files[0]);
    reader.onload = function (e) {
      if (imgRef.current) imgRef.current.src = e.target.result;
    };
  }

  const hasImage = isImgPreview || imageName;

  return (
    <div className="mcq-image-upload m-t-16" name={name}>
      <b className="mcq-image-upload__label">
        {label}{' '}
        {optional
          ? <span className="mcq-image-upload__optional f-14">(optional)</span>
          : <span className="mcq-image-upload__required f-14">(required)</span>}
      </b>

      {/* Dropzone — only when no image */}
      {!hasImage && (
        <label className="mcq-image-upload__dropzone pointer" cy={name}>
          Upload image
          <input type="file" accept="image/jpeg,image/png" onChange={handleUpload} />
        </label>
      )}

      {/* Beige preview box — only when image exists */}
      {hasImage && (
        <div className="mcq-image-upload__preview-box" cy={name}>
          {imageName && !isImgPreview && (
            <img className="mcq-image-upload__img" src={buildLepImageUrl(imageName, imageLocation )} alt={altText} />
          )}
          <img
            ref={imgRef}
            className={`mcq-image-upload__img${isImgPreview ? '' : ' hide'}`}
            alt={altText}
            onLoad={() => {
              const questionElm = imgRef.current?.closest('.base-question');
              if (questionElm && questionElm.style.height && questionElm.style.height !== 'auto') {
                questionElm.style.height = `${questionElm.scrollHeight}px`;
              }
            }}
          />
        </div>
      )}

      {/* Delete link — below preview, right-aligned */}
      {hasImage && (
        <div className="mcq-image-upload__delete-row">
          <span
            className="mcq-image-upload__delete pointer"
            cy="delete-img"
            onClick={handleDelete}
          >
            Delete
          </span>
        </div>
      )}

      {fileError && (
        <p className="mcq-image-upload__file-error" role="alert">
          {fileError.replace(/\*\*(.*?)\*\*/g, (_, t) => t)}
        </p>
      )}
      {fileWarning && !fileError && (
        <p className="mcq-image-upload__file-warning" role="status">
          {fileWarning.replace(/\*\*(.*?)\*\*/g, (_, t) => t)}
        </p>
      )}

      {hasImage && (
        <div className="mcq-image-upload__alt m-t-8">
          <label>
            Alt text <span className="mcq-image-upload__required f-14">(required)</span>
            <input
              type="text"
              placeholder="Describe the image for screen reader users..."
              value={altText}
              maxLength={125}
              onChange={(e) =>
                onAltTextChange && onAltTextChange({ key: `${name}Alt`, value: e.target.value })
              }
            />
          </label>
        </div>
      )}
    </div>
  );
};
